<template>
  <div>
    <b-tabs vertical :animated="false" class="settingTabs" v-model="settingTab">
      <b-tab-item label="General">
        <div :class="$style.auditSettingsEditContainer">
          <label class="label label-settings-lineHeight label-color"
            >Data Collection Period Title</label
          >
          <b-input
            :disabled="!editable"
            v-model="auditSettingDetail.name"
            name="audit_name"
            @input="updateDisableSettingsButton('auditName')"
            custom-class="audit-settings-general-input"
          ></b-input>

          <label
            class="label label-settings-lineHeight label-color margin-top20"
            >Data Collection Period Description</label
          >
          <b-input
            :disabled="!editable"
            placeholder="Enter Data Collection Period Description"
            v-model="auditSettingDetail.description"
            name="audit_description"
            type="textarea"
            @input="updateDisableSettingsButton('auditDescription')"
            custom-class="audit-settings-general-input"
          >
          </b-input>
          <br />
          <div :class="$style.calendarContainer">
            <b-field label="Data Collection Period">
              <b-datepicker
                v-if="isCycleTypeAudit"
                range
                v-model="dataCollectionPeriodDate"
                @input="updateDisableSettingsButton('dataCollectionDate')"
                placeholder="Click to select..."
                icon="calendar-today"
                icon-pack="mdi"
                :class="[$style.customCalendarIcon, 'calender-icon']"
                nearby-selectable-month-days
                :date-formatter="formatDataCollectionDateRange"
                :disabled="isAuditCycleCommencedOrCompleted"
              >
              </b-datepicker>
              <b-datepicker
                v-else
                placeholder="Select a start date"
                icon="calendar-today"
                icon-pack="mdi"
                v-model="generalStartDate"
                @input="updateDisableSettingsButton('dataCollectionDate')"
                class="has-icon-right input-grey datepicker-input"
                :date-formatter="formatDateRange"
              >
              </b-datepicker>
            </b-field>
          </div>
          <hr />
          <div class="margin-top60">
            <a
              v-if="isAuditArchived"
              @click="handleUnarchiveAudit"
              class="archive-button text-size bold"
            >
              Unarchive Data Collection Period
            </a>
            <a
              v-else
              @click="handleArchiveAudit"
              class="archive-button text-size bold"
            >
              Archive Data Collection Period
            </a>
            <a
              @click="handleDeleteAudit"
              :class="[$style.deleteButton, `archive-button text-size bold`]"
            >
              Delete Data Collection Period
            </a>
            <b-button
              v-if="editable"
              :disabled="isGeneralSettingSubmitButtonDisabled"
              @click="updateGeneralSettingHandler"
              :class="$style.saveChangesBtn"
              type="is-primary"
            >
              Save
            </b-button>
          </div>
        </div>
      </b-tab-item>

      <b-tab-item label="Data Collection">
        <div :class="$style.auditSettingsEditContainer">
          <b-field label="Data Timestamp">
            <b-dropdown
              aria-role="list"
              v-model="auditDataTimestamp"
              class="data-timestamp-dropdown-container"
              :disabled="!editable"
            >
              <button
                class="button data-timestamp-dropdown"
                slot="trigger"
                slot-scope="{ active }"
              >
                <span>{{ auditDataTimestamp }}</span>
                <b-icon :icon="active ? 'menu-up' : 'menu-down'"></b-icon>
              </button>

              <b-dropdown-item
                aria-role="listitem"
                value="Single Date"
                class="data-timestamp-dropdown-item"
              >
                Single Date
              </b-dropdown-item>
              <b-dropdown-item
                aria-role="listitem"
                value="Single Date with Time"
                class="data-timestamp-dropdown-item"
              >
                Single Date with Time
              </b-dropdown-item>
              <b-dropdown-item
                aria-role="listitem"
                value="Date Range"
                class="data-timestamp-dropdown-item"
              >
                Date Range
              </b-dropdown-item>
              <b-dropdown-item
                aria-role="listitem"
                value="Date Range with Time"
                class="data-timestamp-dropdown-item"
              >
                Date Range with Time
              </b-dropdown-item>
              <b-dropdown-item
                aria-role="listitem"
                value="None"
                class="data-timestamp-dropdown-item"
                >None
              </b-dropdown-item>
            </b-dropdown>
          </b-field>
          <b-button
            v-if="editable"
            @click="actionDataCollectionAudit(isDataCollectionActive)"
            :class="[
              $style.actionDataCollectionAudit,
              isDataCollectionActive
                ? $style.stopDataCollectionAudit
                : $style.resumeDataCollectionAudit,
            ]"
          >
            {{
              isDataCollectionActive
                ? 'Stop Data Collection'
                : 'Resume Data Collection'
            }}
          </b-button>
          <div class="columns">
            <div class="column is-two-thirds vertical-center text-color"></div>
            <div class="column is-one-third" v-if="editable">
              <b-button
                :disabled="isDataCollectionSubmitButtonDisabled"
                @click="updateDataCollectionSettingHandler"
                :class="$style.saveChangesBtn"
                type="is-primary"
              >
                Save
              </b-button>
            </div>
          </div>
        </div>
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import { RootState } from '@/store/store';
import {
  MultipleRecurrenceFrequencyOptions,
  ProjectStatus,
} from '@/store/modules/projects/types/projects.types';
import {
  AuditDetail,
  AuditStatus,
  UpdateAuditGeneralSettingRequestPayload,
  UpdateAuditDataCollectionSettingRequestPayload,
  UpdateAuditStatusPayload,
  ResumeEndDatePayload,
  DeletedAuditDTO,
} from '@/store/modules/audits/types/audits.types';
import { isTruthy, isDifferent } from '@/jbi-shared/util/watcher.vue-decorator';
import { isEqual as _isEqual } from 'lodash';
import { ToastProgrammatic as Toast } from 'buefy';
import BaseDialog from '../../../components/base/BaseDialog.vue';
import ArchiveModalBox from '../../../components/ArchiveModalBox.vue';
import SiteSettings from '@/views/Project/components/SiteSettings.vue';
import { cloneDeep as _cloneDeep, map as _map } from 'lodash';
import SettingActiveDataCollectionDialog from './SettingActiveDataCollectionDialog.vue';
import moment from 'moment';
import { getEndDateTime, getStartDateTime } from '../../../utils/date.util';

const dirtyMonths: string[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

@Component({
  components: {
    BaseDialog,
    SiteSettings,
  },
})
export default class AuditSettingsTab extends Vue {
  @Prop() public auditId!: number;
  @Prop() public auditDetail!: AuditDetail;
  @Prop() public auditSettingInfo!: any;
  @Prop({ type: Boolean, default: false })
  public isAuditCycleCommencedOrCompleted!: boolean;

  public settingTab: number = 0;
  public isGeneralSettingSubmitButtonDisabled: boolean = true;
  public isDataCollectionSubmitButtonDisabled: boolean = true;
  public auditDataTimestamp: string = '';
  public auditDetailInfo: any = [];
  public auditSettingDetail: {
    name: string;
    description: string;
    enableGrip: boolean;
    startDate: Date | string | null;
    endDate: Date | string | null;
  } = {
    name: '',
    description: '',
    enableGrip: false,
    startDate: null,
    endDate: null,
  };
  public isDataCollectionActive: boolean = false;
  public isCycleTypeAudit: boolean = false;
  public dataCollectionPeriodDate: Date[] = [];
  public generalStartDate: Date = getStartDateTime();
  public generalEndDate: Date | null = getEndDateTime();
  public auditStatus: AuditStatus = AuditStatus.ACTIVE;
  public updateAuditStatusPayload: UpdateAuditStatusPayload = {
    auditId: 0,
    status: AuditStatus.ACTIVE,
  };

  @Action('audits/updateAuditGeneralSettings')
  public updateAuditGeneralSettings!: (
    params: UpdateAuditGeneralSettingRequestPayload,
  ) => void;

  @Action('audits/updateAuditDataCollectionSettings')
  public updateAuditDataCollectionSettings!: (
    params: UpdateAuditDataCollectionSettingRequestPayload,
  ) => void;

  @Action('audits/getAuditDetail')
  public getAuditDetail!: (auditId: number) => void;

  @Action('audits/updateStatus')
  public updateAuditStatus!: (params: UpdateAuditStatusPayload) => void;

  @Action('audits/deleteAudit')
  public deleteAudit!: (auditId: number) => void;

  @Action('audits/endAudit')
  public endAudit!: (auditId: number) => void;

  @Action('audits/resumeAudit')
  public resumeAudit!: (params: ResumeEndDatePayload) => void;

  @State((state: RootState) => state.audits.apiState.updateStatus.success)
  public updateStatusSuccess!: boolean;

  @State((state: RootState) => state.audits.apiState.deleteAudit.success)
  public deleteAuditStatusSuccess!: boolean;

  @State((state: RootState) => state.audits.deletedAudit)
  public deletedAudit!: DeletedAuditDTO;

  @State((state: RootState) => state.audits.dataCollectionPeriodTitle)
  public dataCollectionPeriodTitle!: any;

  @State((state: RootState) => state.audits.updatedAuditGeneralSettings)
  public updatedAuditGeneralSettings!: any;

  @State((state: RootState) => state.audits.updatedAuditDataCollectionSettings)
  public updatedAuditDataCollectionSettings!: any;

  @State((state: RootState) => state.audits.apiState.endAudit.success)
  public endDataCollectionAuditSuccess!: boolean;

  @State((state: RootState) => state.audits.apiState.resumeAudit.success)
  public resumeDataCollectionAuditSuccess!: boolean;

  get isAuditArchived(): boolean {
    return this.auditStatus === AuditStatus.ARCHIVED;
  }

  get editable(): boolean {
    if (
      this.auditDetail?.project?.status === 'Archived' ||
      this.auditStatus === AuditStatus.ARCHIVED
    ) {
      return false;
    }
    return true;
  }

  public created(): void {
    this.initializeFromProps();
  }

  public initializeFromProps(): void {
    if (this.auditDetail) {
      this.auditStatus = this.auditDetail.status ?? AuditStatus.ACTIVE;
      this.updateAuditStatusPayload = {
        auditId: this.auditId,
        status: this.auditStatus,
      };
    }
    if (
      this.auditSettingInfo &&
      Object.keys(this.auditSettingInfo).length > 0
    ) {
      this.auditSettingDetail = _cloneDeep(this.auditSettingInfo);
      this.auditDetailInfo = _cloneDeep(this.auditDetail);
    }
  }

  public updateDisableSettingsButton(type: string) {
    if (type === 'auditDescription') {
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        this.auditSettingInfo.description,
        this.auditSettingDetail.description,
      );
    } else if (type === 'auditName') {
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        this.auditSettingInfo.name,
        this.auditSettingDetail.name,
      );
    } else if (type === 'dataCollectionDate') {
      const auditDataCollectionDate = [
        getStartDateTime(this.auditDetail.startDate),
        getEndDateTime(this.auditDetail.endDate),
      ];
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        auditDataCollectionDate,
        this.dataCollectionPeriodDate,
      );
    } else {
      this.isGeneralSettingSubmitButtonDisabled = _isEqual(
        this.auditSettingInfo.enableGrip,
        this.auditSettingDetail.enableGrip,
      );
    }
  }

  public updateGeneralSettingHandler() {
    this.generalStartDate = this.isCycleTypeAudit
      ? getStartDateTime(this.dataCollectionPeriodDate[0])
      : this.generalStartDate;

    this.generalEndDate = this.isCycleTypeAudit
      ? getEndDateTime(this.dataCollectionPeriodDate[1])
      : null;

    this.updateAuditGeneralSettings({
      auditId: this.auditId,
      auditName: this.auditSettingDetail.name,
      auditDescription: this.auditSettingDetail.description,
      enableGrip: this.auditSettingDetail.enableGrip,
      startDate: this.generalStartDate,
      endDate: this.generalEndDate as Date, // FIXME: This is a hack. Null cases need to be handled properly.
    });
  }

  public updateDataCollectionSettingHandler() {
    this.updateAuditDataCollectionSettings({
      auditId: this.auditId,
      auditDataTimestamp: this.auditDataTimestamp,
    });
  }

  public getDate(newValue: Date): string {
    return (
      newValue.getDate() +
      ' ' +
      dirtyMonths[newValue.getMonth()] +
      ' ' +
      newValue.getFullYear()
    );
  }

  public formatDateRange(date: Date): string {
    return this.getDate(date);
  }

  public formatDataCollectionDateRange(date: Date[]): string {
    return this.getDate(date[0]) + ' - ' + this.getDate(date[1]);
  }

  public mounted() {
    this.initializeFromProps();
    this.auditDetailInfo = _cloneDeep(this.auditDetail);
    this.auditSettingDetail = _cloneDeep(this.auditSettingInfo);
    this.auditDataTimestamp = this.auditDetailInfo.auditDataTimestamp;
    this.isDataCollectionActive = this.auditDetailInfo.endedAt ? false : true;
    this.isCycleTypeAudit =
      this.auditDetailInfo.auditCategory === 'Cycle' ? true : false;
    this.generalStartDate =
      this.auditDetailInfo.auditCategory === 'Cycle'
        ? moment(this.auditSettingDetail.startDate).toDate()
        : getStartDateTime(this.auditSettingDetail.startDate);

    this.generalEndDate = null;
    if (this.auditDetailInfo.auditCategory === 'Cycle') {
      if (this.auditSettingDetail.endDate) {
        this.generalEndDate = getEndDateTime(this.auditSettingDetail.endDate);
      } else {
        const endDate = moment(this.auditSettingDetail.startDate);
        switch (
          this.auditDetailInfo.project.settings[0].multipleRecurrenceType
        ) {
          case Object.keys(MultipleRecurrenceFrequencyOptions)[0]:
            endDate.add(this.auditDetailInfo.project.settings[0].interval, 'd');
            break;
          case Object.keys(MultipleRecurrenceFrequencyOptions)[1]:
            endDate.add(this.auditDetailInfo.project.settings[0].interval, 'w');
            break;
          case Object.keys(MultipleRecurrenceFrequencyOptions)[2]:
            endDate.add(this.auditDetailInfo.project.settings[0].interval, 'M');
            break;
          default:
            endDate.add(this.auditDetailInfo.project.settings[0].interval, 'y');
            break;
        }
        const newEndDate = endDate.subtract(1, 'd');
        this.generalEndDate = endDate.isBefore(new Date())
          ? getEndDateTime()
          : getEndDateTime(newEndDate);
      }
    }
    this.dataCollectionPeriodDate = [
      this.generalStartDate,
      this.generalEndDate as Date, // FIXME: This is a hack. Null cases need to be handled properly.
    ];
  }

  public handleArchiveAudit() {
    this.$buefy.modal.open({
      parent: this,
      component: ArchiveModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Archive Data Collection Period?',
        modalContent: `Archiving ${this.auditSettingDetail.name} will disable any ongoing data collection activies. You can find archived data collection in Project Dashboard under Data Collection Period.`,
      },
      events: {
        confirm: () => {
          this.updateAuditStatusPayload.status = AuditStatus.ARCHIVED;
          this.updateAuditStatus(this.updateAuditStatusPayload);
        },
      },
    });
  }

  public handleUnarchiveAudit() {
    this.$buefy.modal.open({
      parent: this,
      component: BaseDialog,
      hasModalCard: true,
      trapFocus: true,
      props: {
        title: `Unarchive Data Collection Period`,
        content:
          'This will enable data collection period to be edited. You may archive it again anytime',
        cancelButtonText: 'Unarchive',
        confirmButtonText: 'Cancel',
      },
      events: {
        cancel: () => {
          this.updateAuditStatusPayload.status = AuditStatus.ACTIVE;
          this.updateAuditStatus(this.updateAuditStatusPayload);
        },
      },
    });
  }

  public handleDeleteAudit() {
    this.$buefy.modal.open({
      parent: this,
      component: BaseDialog,
      hasModalCard: true,
      trapFocus: true,
      props: {
        title: `Delete Data Collection Period?`,
        content: `Deleting '${this.auditSettingDetail.name}' will remove it completely from PACES and you can’t access this data collection period.`,
        confirmButtonText: 'Delete',
      },
      events: {
        confirm: () => {
          this.updateAuditStatusPayload.status = AuditStatus.DELETED;
          this.deleteAudit(this.auditId);
        },
      },
    });
  }

  @Watch('updateStatusSuccess')
  @isTruthy
  public watchUpdatedStatus(): void {
    Toast.open({
      queue: true,
      type: 'is-dark',
      position: 'is-top',
      message: `Changes saved`,
    });
    this.auditStatus = this.updateAuditStatusPayload.status as AuditStatus;
    this.$emit('updatedAuditStatus', this.auditStatus);
    if (this.auditStatus === AuditStatus.DELETED) {
      this.$router.push({
        name: 'project-details',
        params: {
          projectId: this.auditDetail.project.id.toString(),
        },
      });
    }
  }

  @Watch('deleteAuditStatusSuccess')
  @isTruthy
  public watchDeleteAuditStatus(): void {
    this.auditStatus = this.deletedAudit.auditStatus as AuditStatus;
    this.$emit('updatedAuditStatus', this.auditStatus);

    let toastMsg = `Changes saved`;
    if (this.auditStatus === AuditStatus.DELETED) {
      // If project status is draft redirect to draft-list else redirect to project details
      if (this.deletedAudit.projectStatus === ProjectStatus.DRAFT) {
        toastMsg = 'Project has been saved as draft.';
        this.$router.push({
          name: 'project-me',
          query: {
            tab: 'Draft',
          },
        });
      } else {
        this.$router.push({
          name: 'project-details',
          params: {
            projectId: this.auditDetail.project.id.toString(),
          },
        });
      }
    }

    Toast.open({
      queue: true,
      type: 'is-dark',
      position: 'is-top',
      message: toastMsg,
    });
  }

  @Watch('updatedAuditGeneralSettings')
  @isTruthy
  public updateSavedGeneralSettings(newValue: any) {
    this.isGeneralSettingSubmitButtonDisabled = true;
    const updatedGeneralData = {
      name: newValue.name,
      description: newValue.description,
      enableGrip: newValue.enableGrip,
      startDate: this.generalStartDate,
      endDate: this.generalEndDate,
    };
    this.$emit('updateAuditSettings', updatedGeneralData);
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
    });
  }

  @Watch('updatedAuditDataCollectionSettings')
  @isTruthy
  public updateSavedDataCollectionSettings(newValue: any) {
    this.isDataCollectionSubmitButtonDisabled = true;
    this.getAuditDetail(this.auditId);
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
    });
  }

  @Watch('endDataCollectionAuditSuccess')
  @isTruthy
  public watchEndDataCollectionAuditSuccess(newValue: any) {
    this.isDataCollectionActive = false;
    Toast.open({
      queue: true,
      type: 'is-dark',
      position: 'is-top',
      message: 'Changes saved',
    });
  }

  @Watch('resumeDataCollectionAuditSuccess')
  @isTruthy
  public watchResumeDataCollectionAuditSuccess(newValue: any) {
    this.isDataCollectionActive = true;
    Toast.open({
      queue: true,
      type: 'is-dark',
      position: 'is-top',
      message: 'Changes saved',
    });
  }

  @Watch('auditDataTimestamp')
  @isTruthy
  @isDifferent
  public watchAuditDataTimestamp(newValue: any) {
    this.isDataCollectionSubmitButtonDisabled = _isEqual(
      this.auditDetail.auditDataTimestamp,
      newValue,
    );
  }

  public actionDataCollectionAudit(isActive: boolean) {
    let title: string;
    let content: string;
    let confirmButtonText: string;
    if (isActive) {
      title = 'Stop Data Collection?';
      content = `Stopping data collection for '${this.auditSettingDetail.name}' will disable anyone from enterring data.`;
      confirmButtonText = 'Stop';
    } else {
      title = 'Resume Data Collection?';
      content = `Resuming data collection for '${this.auditSettingDetail.name}' will enable data entry.`;
      confirmButtonText = 'Resume';
    }
    this.$buefy.modal.open({
      parent: this,
      component: SettingActiveDataCollectionDialog,
      hasModalCard: true,
      trapFocus: true,
      props: {
        title,
        content,
        confirmButtonText,
        isActive,
        resumeEndDate: this.generalEndDate,
      },
      events: {
        confirm: (newValue: any) => {
          this.dataCollectionPeriodDate = [this.generalStartDate, newValue];
          if (isActive) {
            this.endAudit(this.auditId);
          } else {
            this.resumeAudit({
              auditId: this.auditId,
              endDate: newValue,
            });
          }
        },
      },
    });
  }
}
</script>

<style lang="scss" module>
.auditSettingsEditContainer {
  width: 680px;
  margin-right: auto;
  margin-left: 20px;
}
.saveChangesBtn {
  float: right;
  border-radius: 24px;
  height: 48px;
  padding: 0 calc(1em + 0.25em);
  font-weight: 700 !important;
  font-size: 18px !important;
  line-height: 22px !important;
  bottom: 20px;
}
.actionDataCollectionAudit {
  background: unset;
  border: unset;
  padding-left: 0px;
  margin-bottom: 30px;
  span {
    font-size: 14px;
  }
}
.stopDataCollectionAudit {
  color: #ff7171;
  &:hover {
    span {
      color: #ff7171;
    }
  }
}
.resumeDataCollectionAudit {
  color: #00205b;
  &:hover {
    span {
      color: #00205b;
    }
  }
}
.deleteButton {
  color: #ff7171;
}
.deleteButton:hover {
  color: red;
}
.calendarContainer {
  -webkit-box-flex: 0;
  display: block;
  flex: none;
  width: 50%;
}
.customCalendarIcon {
  i::before {
    padding-top: 13px;
    padding-bottom: 12px;
    width: 15px;
    margin-right: 10px;
  }
}
</style>
